"use client";

import { createContext, useContext, type ComponentType } from "react";

/**
 * Slots de UI injetados no chat por módulos de feature.
 *
 * Por que injeção e não import direto: `core/` é infra compartilhada, consumida por
 * `insights`/`dashboard`/etc. Importar `@/insights/**` aqui inverteria a dependência do
 * módulo (hoje o único import nessa direção é um lazy de rota em `core/routes/index.tsx`).
 * O chat declara o contrato; quem monta o `ChatbotProvider` fornece a implementação.
 *
 * Os tipos são propositalmente ESTRUTURAIS (não importam `NotificationSchedule` nem
 * `ScheduleFormValues` de `insights`): o componente concreto refina no seu próprio módulo.
 * Slot ausente → o block não renderiza, coerente com a regra de ouro do renderer
 * (desconhecido degrada em silêncio, ver `parseBlocks`).
 */

/** Dados crus de um agendamento, como vêm de `/notification-schedules`. */
export type RawSchedule = Record<string, unknown>;

export interface ScheduleFormSlotProps {
  /** `null`/ausente = criação; presente = edição pré-preenchida. */
  schedule?: RawSchedule | null;
  /** Encerra o form sem chamar a API. */
  onDone: () => void;
}

export interface ScheduleListSlotProps {
  /**
   * Seed vindo do block. O componente deve preferir o cache da query
   * (`["report-schedules"]`) quando disponível, para refletir alterações feitas na tela
   * de Insights sem depender de um novo turno do agente.
   */
  schedules: RawSchedule[];
}

export interface ChatSlots {
  schedule_form?: ComponentType<ScheduleFormSlotProps>;
  schedule_list?: ComponentType<ScheduleListSlotProps>;
}

const ChatSlotsContext = createContext<ChatSlots>({});

export function ChatSlotsProvider({
  slots,
  children,
}: {
  slots?: ChatSlots;
  children: React.ReactNode;
}) {
  return (
    <ChatSlotsContext.Provider value={slots ?? {}}>
      {children}
    </ChatSlotsContext.Provider>
  );
}

export function useChatSlots(): ChatSlots {
  return useContext(ChatSlotsContext);
}
